/*
Purpose: "Print PDF" pop-up for tutor timesheets -- choose all tutors or a single tutor, and
         optionally a month, then download the PDF. Shared so the same choices are available on
         both /ListTutorTimeSheet and /report-finance-timesheet.
Project: TCMS
*/

import { useEffect, useMemo, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import axios from "axios";
import Moment from "moment";
import { security_key, BASE_URL } from "../global";
import { downloadTimesheetsPdf, timesheetInMonth, timesheetMonthOptions } from "./TimesheetPrint";

const tutorFullName = (ts) => [ts.tut_fname, ts.tut_surname].filter(Boolean).join(" ") || "-";

/**
 * @param {object}  props
 * @param {boolean} props.show
 * @param {func}    props.onHide
 * @param {string} [props.defaultTutorKey] pre-selects "Single tutor" with this tutor
 * @param {string} [props.defaultMonth]    'YYYY-MM' to pre-select, '' for all months
 */
function PrintTimesheetModal({ show, onHide, defaultTutorKey = "", defaultMonth = "" }) {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const [scope, setScope] = useState("all"); // 'all' | 'single'
  const [tutorKey, setTutorKey] = useState("");
  const [month, setMonth] = useState("");

  // Reset the choices and load every tutor's timesheets each time the pop-up opens, so it
  // reflects anything marked paid/edited since it was last opened.
  useEffect(() => {
    if (!show) return;
    setScope(defaultTutorKey ? "single" : "all");
    setTutorKey(defaultTutorKey);
    setMonth(defaultMonth);
    fetchTimesheets();
  }, [show]);

  const fetchTimesheets = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getTutorTimesheetReportCenter",
        fk_cent_key: sessionStorage.getItem("key"),
      });
      if (response.data.success === true) {
        setTimesheets(response.data.data || []);
      } else {
        setTimesheets([]);
        setLoadFailed(true);
      }
    } catch (error) {
      console.error("Error fetching timesheets:", error);
      setTimesheets([]);
      setLoadFailed(true);
    }
    setLoading(false);
  };

  const tutorOptions = useMemo(() => {
    const byKey = new Map();
    timesheets.forEach((ts) => byKey.set(ts.pk_tut_key, tutorFullName(ts)));
    return [...byKey.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [timesheets]);

  const scoped = scope === "single" ? timesheets.filter((ts) => ts.pk_tut_key === tutorKey) : timesheets;
  const monthOptions = timesheetMonthOptions(scoped);
  const toPrint = scoped.filter((ts) => timesheetInMonth(ts, month));
  const canDownload = !loading && toPrint.length > 0 && (scope === "all" || tutorKey);

  const handleDownload = () => {
    if (!canDownload) return;
    const monthLabel = monthOptions.find((m) => m.value === month)?.label;
    const tutorLabel = scope === "single" ? tutorOptions.find((t) => t.value === tutorKey)?.label || "Tutor" : "All tutors";
    const sortName = (ts) => tutorFullName(ts).toLowerCase();

    // Grouped by tutor, newest timesheet first within each tutor -- the same order as the grids.
    downloadTimesheetsPdf({
      timesheets: [...toPrint].sort(
        (a, b) =>
          sortName(a).localeCompare(sortName(b)) ||
          String(b.timesheet_from_date).localeCompare(String(a.timesheet_from_date))
      ),
      title: monthLabel ? `Timesheets - ${monthLabel}` : "Timesheets",
      subtitle: tutorLabel,
      fileName: `Timesheets_${tutorLabel.replace(/\s+/g, "_")}${month ? `_${month}` : ""}`,
    });
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <h5 className="page-title">Print Timesheets</h5>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <LinearProgress className="mb-3" />}
        {loadFailed && <p className="text-danger">Could not load timesheets. Please try again.</p>}

        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Print</Form.Label>
          <Form.Check
            type="radio"
            id="print-scope-all"
            name="print-scope"
            label="All tutors"
            checked={scope === "all"}
            onChange={() => setScope("all")}
          />
          <Form.Check
            type="radio"
            id="print-scope-single"
            name="print-scope"
            label="Single tutor"
            checked={scope === "single"}
            onChange={() => setScope("single")}
          />
        </Form.Group>

        {scope === "single" && (
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Tutor</Form.Label>
            <Form.Select value={tutorKey} onChange={(e) => setTutorKey(e.target.value)} disabled={loading}>
              <option value="">Select a tutor</option>
              {tutorOptions.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        <Form.Group className="mb-2">
          <Form.Label className="fw-bold">Month</Form.Label>
          <Form.Select value={month} onChange={(e) => setMonth(e.target.value)} disabled={loading}>
            <option value="">All months</option>
            {/* Keep a pre-selected month listed even if this tutor has nothing in it. */}
            {month && !monthOptions.some((m) => m.value === month) && (
              <option value={month}>{Moment(month, "YYYY-MM").format("MMMM YYYY")}</option>
            )}
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {!loading && !loadFailed && (scope === "all" || tutorKey) && (
          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
            {toPrint.length === 0
              ? "No timesheets match this selection."
              : `${toPrint.length} timesheet${toPrint.length === 1 ? "" : "s"} will be included.`}
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="mx-auto d-flex gap-2">
          <Button variant="contained" className="primary-gray" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="contained" className="primary-filled" onClick={handleDownload} disabled={!canDownload}>
            Download PDF
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default PrintTimesheetModal;
